import { useEffect, useState } from 'react';
import api from '@/utils/api';
import { toast } from 'sonner';
import { BarChart3, Users, Globe } from 'lucide-react';

// Aug 2026 — First-Party-Nutzungsanalyse (ersetzt PostHog): Aktivierungs-Funnel,
// Feature-Adoption, aktive User, anonyme Landing-Statistik. Alles aus eigener Mongo.
export default function AdminUsagePanel() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/admin/usage-analytics')
      .then((r) => setData(r.data))
      .catch(() => toast.error('Nutzungsdaten konnten nicht geladen werden'));
  }, []);

  if (!data) return <p className="text-gray-500 py-8">Lade Nutzungsdaten …</p>;

  const funnelMax = Math.max(1, ...(data.activation_funnel || []).map((f) => f.count));

  return (
    <div className="space-y-6" data-testid="admin-usage-panel">
      <div>
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-emerald-600" /> Nutzung (First-Party, consent-frei)
        </h2>
        <p className="text-sm text-gray-500">{data.note}</p>
      </div>

      {/* Aktive User */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="rounded-xl border bg-white p-4">
          <p className="text-xs text-gray-500 flex items-center gap-1"><Users className="h-3.5 w-3.5" />Aktiv 7 Tage</p>
          <p className="text-2xl font-semibold" data-testid="usage-active-7d">{data.active_users.d7}</p>
        </div>
        <div className="rounded-xl border bg-white p-4">
          <p className="text-xs text-gray-500 flex items-center gap-1"><Users className="h-3.5 w-3.5" />Aktiv 30 Tage</p>
          <p className="text-2xl font-semibold" data-testid="usage-active-30d">{data.active_users.d30}</p>
        </div>
        <div className="rounded-xl border bg-white p-4">
          <p className="text-xs text-gray-500 flex items-center gap-1"><Globe className="h-3.5 w-3.5" />Seitenaufrufe 30 T. (anonym)</p>
          <p className="text-2xl font-semibold" data-testid="usage-pageviews">{data.web_30d.pageviews}</p>
        </div>
        <div className="rounded-xl border bg-white p-4">
          <p className="text-xs text-gray-500">Zahlende / Registrierte</p>
          <p className="text-2xl font-semibold" data-testid="usage-paying">
            {data.activation_funnel[3]?.count} / {data.activation_funnel[0]?.count}
          </p>
        </div>
      </div>

      {/* Aktivierungs-Funnel */}
      <div className="rounded-xl border bg-white p-4" data-testid="usage-funnel">
        <h3 className="text-sm font-semibold mb-3">Aktivierungs-Funnel (Bestandsdaten, ivoliq)</h3>
        <div className="space-y-2">
          {data.activation_funnel.map((f, i) => (
            <div key={f.step} className="flex items-center gap-3 text-sm">
              <span className="w-44 shrink-0 text-slate-600">{f.step}</span>
              <div className="flex-1 bg-slate-100 rounded h-6 overflow-hidden">
                <div className="h-6 bg-emerald-600/80 rounded"
                     style={{ width: `${Math.max(2, (f.count / funnelMax) * 100)}%` }} />
              </div>
              <span className="w-20 text-right font-mono">{f.count}
                {i > 0 && data.activation_funnel[i - 1].count >= f.count && data.activation_funnel[i - 1].count > 0 && (
                  <span className="text-xs text-slate-400"> ({Math.round((f.count / data.activation_funnel[i - 1].count) * 100)}%)</span>
                )}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Feature-Adoption */}
      <div className="rounded-xl border bg-white p-4" data-testid="usage-features">
        <h3 className="text-sm font-semibold mb-2">Feature-Nutzung (letzte 30 Tage, serverseitige Events)</h3>
        {data.features_30d.length === 0 ? (
          <p className="text-sm text-gray-500" data-testid="usage-features-empty">
            Noch keine Events — das Tracking läuft ab jetzt automatisch bei jeder Nutzeraktion
            (Belege, Rechnungen, Reisen, UVA, Exporte …).
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-gray-500 border-b">
              <tr><th className="py-1.5">Event</th><th className="py-1.5 text-right">Aktionen</th><th className="py-1.5 text-right">Eindeutige User</th></tr>
            </thead>
            <tbody>
              {data.features_30d.map((f) => (
                <tr key={f.event} className="border-b last:border-0">
                  <td className="py-1.5 font-medium">{f.event}</td>
                  <td className="py-1.5 text-right font-mono">{f.count}</td>
                  <td className="py-1.5 text-right font-mono">{f.unique_users}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Onboarding + Löschungen */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="rounded-xl border bg-white p-4" data-testid="usage-onboarding">
          <h3 className="text-sm font-semibold mb-2">Onboarding-Funnel (90 T., je Wizard-Schritt)</h3>
          {(!data.onboarding_90d || data.onboarding_90d.steps.length === 0) ? (
            <p className="text-sm text-gray-500" data-testid="usage-onboarding-empty">
              Noch keine Schritt-Events — werden ab jetzt bei jedem „Weiter"/„Überspringen" im Wizard erfasst.
            </p>
          ) : (
            <>
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-gray-500 border-b">
                  <tr><th className="py-1.5">Schritt</th><th className="py-1.5 text-right">Abgeschlossen</th><th className="py-1.5 text-right">Übersprungen</th></tr>
                </thead>
                <tbody>
                  {data.onboarding_90d.steps.map((s) => (
                    <tr key={s.step} className="border-b last:border-0">
                      <td className="py-1.5">{Number.isFinite(Number(s.step)) ? `Schritt ${Number(s.step) + 1}` : String(s.step)}</td>
                      <td className="py-1.5 text-right font-mono">{s.completed}</td>
                      <td className="py-1.5 text-right font-mono text-amber-600">{s.skipped}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-xs text-gray-500 mt-2">Wizard komplett abgeschlossen: <strong>{data.onboarding_90d.completed_users_90d}</strong> User</p>
            </>
          )}
        </div>
        <div className="rounded-xl border bg-white p-4" data-testid="usage-deletions">
          <h3 className="text-sm font-semibold mb-2">
            Konto-Löschungen (90 T.: {data.deletions?.count_90d ?? 0})
          </h3>
          {(!data.deletions || data.deletions.recent.length === 0) ? (
            <p className="text-sm text-gray-500" data-testid="usage-deletions-empty">Keine Löschungen erfasst.</p>
          ) : (
            <div className="space-y-2">
              {data.deletions.recent.map((d, i) => (
                <div key={i} className="text-sm border-b last:border-0 pb-1.5" data-testid={`usage-deletion-${i}`}>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>{new Date(d.at).toLocaleDateString('de-DE')} · Plan {d.plan}{d.country ? ` · ${d.country}` : ''}{d.account_age_days != null ? ` · ${d.account_age_days} Tage alt` : ''}</span>
                  </div>
                  <p className={d.reason ? 'text-slate-700' : 'text-slate-400 italic'}>
                    {d.reason || 'kein Grund angegeben'}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Landing */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="rounded-xl border bg-white p-4" data-testid="usage-top-pages">
          <h3 className="text-sm font-semibold mb-2">Top-Seiten (30 T., anonym & cookielos)</h3>
          {data.web_30d.top_pages.length === 0
            ? <p className="text-sm text-gray-500">Noch keine Daten.</p>
            : data.web_30d.top_pages.map((p) => (
              <div key={p.page} className="flex justify-between text-sm py-0.5">
                <span className="truncate">{p.page || '/'}</span><span className="font-mono">{p.count}</span>
              </div>))}
        </div>
        <div className="rounded-xl border bg-white p-4" data-testid="usage-top-referrers">
          <h3 className="text-sm font-semibold mb-2">Top-Referrer (30 T.)</h3>
          {data.web_30d.top_referrers.length === 0
            ? <p className="text-sm text-gray-500">Noch keine Daten.</p>
            : data.web_30d.top_referrers.map((r) => (
              <div key={r.referrer} className="flex justify-between text-sm py-0.5">
                <span className="truncate">{r.referrer}</span><span className="font-mono">{r.count}</span>
              </div>))}
        </div>
      </div>

      {/* Jun 2026 — Landing-Widget „Admin-Rechner": Funnel & Verteilungen (anonym, 90 T.) */}
      <WidgetFunnelBlock />
    </div>
  );
}

function WidgetFunnelBlock() {
  const [w, setW] = useState(null);

  useEffect(() => {
    api.get('/admin/widget-analytics')
      .then((r) => setW(r.data))
      .catch(() => {});
  }, []);

  if (!w) return null;
  const funnelMax = Math.max(1, ...(w.funnel || []).map((f) => f.sessions));
  const personaTotal = Object.values(w.personas || {}).reduce((s, n) => s + n, 0);

  return (
    <div className="rounded-xl border bg-white p-4" data-testid="usage-widget-analytics">
      <h3 className="text-sm font-semibold mb-1">Landing-Widget „Admin-Rechner" (90 T., anonym)</h3>
      <p className="text-xs text-gray-500 mb-3">{w.note}</p>

      {/* Funnel: viewed → interacted → persona → cta */}
      <div className="space-y-2 mb-4" data-testid="widget-funnel">
        {w.funnel.map((f) => (
          <div key={f.step} className="flex items-center gap-3 text-sm">
            <span className="w-44 shrink-0 text-slate-600">{f.step}</span>
            <div className="flex-1 bg-slate-100 rounded h-6 overflow-hidden">
              <div className="h-6 bg-emerald-600/80 rounded" style={{ width: `${(f.sessions / funnelMax) * 100}%` }} />
            </div>
            <span className="w-16 text-right font-mono">{f.sessions}</span>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <div className="rounded-lg border p-3">
          <p className="text-xs text-gray-500">Persona-Wahl</p>
          <p className="text-sm font-semibold" data-testid="widget-personas">
            {personaTotal === 0 ? '—' : `${Math.round(((w.personas.order || 0) / personaTotal) * 100)} % Ordnung / ${Math.round(((w.personas.chaos || 0) / personaTotal) * 100)} % Chaos`}
          </p>
        </div>
        <div className="rounded-lg border p-3">
          <p className="text-xs text-gray-500">Ø Schock-Zahl bei CTA</p>
          <p className="text-sm font-semibold" data-testid="widget-avg-loss">
            {w.cta.avg_loss != null ? `${w.cta.avg_loss.toLocaleString('de-AT')} €` : '—'}
          </p>
        </div>
        <div className="rounded-lg border p-3">
          <p className="text-xs text-gray-500">Ø Regler bei CTA</p>
          <p className="text-sm font-semibold">
            {w.cta.avg_invoices != null ? `${w.cta.avg_invoices} Rg. · ${w.cta.avg_rate} €/h` : '—'}
          </p>
        </div>
        <div className="rounded-lg border p-3">
          <p className="text-xs text-gray-500">Länder / Geräte (CTA)</p>
          <p className="text-sm font-semibold">
            {Object.entries(w.cta.countries || {}).map(([k, v]) => `${k} ${v}`).join(' · ') || '—'}
            {Object.keys(w.cta.viewports || {}).length > 0 &&
              ` — ${Object.entries(w.cta.viewports).map(([k, v]) => `${k} ${v}`).join(' / ')}`}
          </p>
        </div>
      </div>

      {/* Tagesaggregate (unbegrenzt aufbewahrt) */}
      {w.daily.length > 0 && (
        <div data-testid="widget-daily">
          <h4 className="text-xs font-semibold text-gray-500 mb-1.5">Tageswerte (Aggregat, dauerhaft)</h4>
          <div className="max-h-40 overflow-y-auto text-xs">
            {w.daily.map((d) => (
              <div key={d.day} className="flex justify-between border-b last:border-0 py-1">
                <span>{d.day}</span>
                <span className="font-mono">
                  {d.sessions} Sessions · {(d.events && d.events.widget_viewed) || 0} Views · {(d.cta && d.cta.count) || 0} CTA
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
